/**
 * Utilities for auto-numbering steps in Steps to Reproduce field.
 */

export const NUMBERED_REGEX = /^\d+\.\s/;
export const BULLET_OR_NUMBER_PREFIX_REGEX = /^(\d+[.)]|[-*•])\s*/;

/**
 * Detects whether the text is currently using numbered steps.
 * Matches if the first non-empty line starts with `^\d+\. `
 */
export const detectNumberedFormat = (text?: string | null): boolean => {
  if (!text) return false;
  const lines = text.split(/\r?\n/);
  const firstNonEmpty = lines.find(line => line.trim().length > 0);
  if (!firstNonEmpty) return false;
  return NUMBERED_REGEX.test(firstNonEmpty);
};

/**
 * Normalizes and numbers all non-empty lines sequentially (1..n).
 * Strips existing prefixes (1., 1), -, *, •) to avoid double numbering.
 * Empty lines are preserved as-is.
 */
export const normalizeAndNumberLines = (text: string): string => {
  if (!text) return '';
  const lines = text.split(/\r?\n/);
  let counter = 1;

  const transformed = lines.map(line => {
    if (line.trim().length === 0) {
      return line;
    }
    const cleanContent = line.replace(BULLET_OR_NUMBER_PREFIX_REGEX, '');
    const result = `${counter}. ${cleanContent}`;
    counter++;
    return result;
  });

  return transformed.join('\n');
};

/**
 * Renumbers only existing numbered lines (1..n) without numbering continuation lines (Shift+Enter).
 * Lines without `^\d+\. ` retain their indentation and content.
 * Empty lines remain as-is.
 */
export const renumberLines = (text: string): string => {
  if (!text) return '';
  const lines = text.split(/\r?\n/);
  let counter = 1;

  const transformed = lines.map(line => {
    if (NUMBERED_REGEX.test(line)) {
      const content = line.replace(NUMBERED_REGEX, '');
      const result = `${counter}. ${content}`;
      counter++;
      return result;
    }
    return line;
  });

  return transformed.join('\n');
};

export interface KeyDownResult {
  handled: boolean;
  newValue?: string;
  newCursorPos?: number;
}

/**
 * Handles Enter, Shift+Enter, and Backspace keys when numbered toggle is ON.
 */
export const handleNumberedKeyDown = (
  e: { key: string; shiftKey: boolean; preventDefault: () => void },
  text: string,
  selectionStart: number,
  selectionEnd: number,
): KeyDownResult => {
  // Normalize text to \n internally for consistent index calculations
  const normalizedText = text.replace(/\r\n/g, '\n');
  const cursor = selectionStart;

  // Find start and end of current line
  const lastNewline = normalizedText.lastIndexOf('\n', cursor - 1);
  const lineStart = lastNewline === -1 ? 0 : lastNewline + 1;
  const nextNewline = normalizedText.indexOf('\n', cursor);
  const lineEnd = nextNewline === -1 ? normalizedText.length : nextNewline;
  const currentLine = normalizedText.slice(lineStart, lineEnd);

  // 1. Enter key
  if (e.key === 'Enter') {
    e.preventDefault();

    // 1a. Shift + Enter: new line without number (continuation line)
    if (e.shiftKey) {
      const before = normalizedText.slice(0, cursor);
      const after = normalizedText.slice(selectionEnd);
      const newValue = `${before}\n${after}`;
      return {
        handled: true,
        newValue,
        newCursorPos: cursor + 1,
      };
    }

    // 1b. Regular Enter:
    // If the current line is just a marker with no text (e.g. `^\d+\.\s*$`)
    const emptyMarkerMatch = currentLine.match(/^(\d+)\.\s*$/);
    if (emptyMarkerMatch) {
      // Remove marker on this line, don't create new number
      // We want to completely eliminate this empty marker line (including its newline)
      let beforeLine = normalizedText.slice(0, lineStart);
      let afterLine = normalizedText.slice(lineEnd);
      if (beforeLine.endsWith('\n')) {
        beforeLine = beforeLine.slice(0, -1);
      }
      if (afterLine.startsWith('\n')) {
        afterLine = afterLine.slice(1);
      }
      const combined = beforeLine ? `${beforeLine}${afterLine ? '\n' + afterLine : ''}` : afterLine;
      const newValue = renumberLines(combined);
      return {
        handled: true,
        newValue,
        newCursorPos: beforeLine.length,
      };
    }

    // If current line has a numbered marker `^\d+\. `
    const markerMatch = currentLine.match(/^(\d+)\.\s/);
    if (markerMatch) {
      const beforeCursor = normalizedText.slice(0, cursor);
      const afterCursor = normalizedText.slice(selectionEnd);

      // Determine next number
      const currentNum = parseInt(markerMatch[1], 10);
      const nextNum = currentNum + 1;
      const newLinePrefix = `${nextNum}. `;

      // Splice in newline + new prefix
      const textWithNewLine = `${beforeCursor}\n${newLinePrefix}${afterCursor}`;

      // Renumber lines from the beginning to ensure all downstream lines are sequential
      const renumbered = renumberLines(textWithNewLine);

      // New cursor position should be right after the new prefix
      const newCursorPos = beforeCursor.length + 1 + newLinePrefix.length;

      return {
        handled: true,
        newValue: renumbered,
        newCursorPos,
      };
    }

    // If current line is not numbered (e.g. continuation line or plain text)
    const before = normalizedText.slice(0, cursor);
    const after = normalizedText.slice(selectionEnd);
    return {
      handled: true,
      newValue: `${before}\n${after}`,
      newCursorPos: cursor + 1,
    };
  }

  // 2. Backspace key
  if (e.key === 'Backspace' && cursor === selectionEnd) {
    const markerMatch = currentLine.match(/^(\d+)\.\s/);
    if (markerMatch) {
      const markerLength = markerMatch[0].length;
      // If cursor is right after the marker `n. `
      if (cursor === lineStart + markerLength) {
        e.preventDefault();
        // Remove marker from this line
        const beforeMarker = normalizedText.slice(0, lineStart);
        const afterMarker = normalizedText.slice(cursor);
        const textWithoutMarker = `${beforeMarker}${afterMarker}`;
        const renumbered = renumberLines(textWithoutMarker);
        return {
          handled: true,
          newValue: renumbered,
          newCursorPos: lineStart,
        };
      }
    }
  }

  return { handled: false };
};

/**
 * Handles pasting text into the textarea at current cursor/selection position.
 * Normalizes multi-line pasted text and prevents duplicate numbering.
 */
export const handleNumberedPaste = (
  pastedText: string,
  currentValue: string,
  selectionStart: number,
  selectionEnd: number,
): { newValue: string; newCursorPos: number } => {
  const normalizedPaste = pastedText.replace(/\r\n/g, '\n');
  const normalizedCurrent = currentValue.replace(/\r\n/g, '\n');

  const before = normalizedCurrent.slice(0, selectionStart);
  const after = normalizedCurrent.slice(selectionEnd);

  // If pasted text has multiple lines, or we are pasting into an empty or numbered context
  const pastedLines = normalizedPaste.split('\n');

  // Find the last numbered line index before the insertion point
  const beforeLines = before.split('\n');
  let lastNumber = 0;
  for (const bLine of beforeLines) {
    const match = bLine.match(/^(\d+)\.\s/);
    if (match) {
      lastNumber = parseInt(match[1], 10);
    }
  }

  // Format pasted lines
  let counter = lastNumber === 0 ? 1 : lastNumber + 1;
  const formattedPasted = pastedLines.map((line, idx) => {
    // If the insertion is continuing the same line and before is not empty
    if (idx === 0 && before.length > 0 && !before.endsWith('\n')) {
      return line.replace(BULLET_OR_NUMBER_PREFIX_REGEX, '');
    }
    if (line.trim().length === 0) {
      return line;
    }
    const clean = line.replace(BULLET_OR_NUMBER_PREFIX_REGEX, '');
    const res = `${counter}. ${clean}`;
    counter++;
    return res;
  }).join('\n');

  const combined = `${before}${formattedPasted}${after}`;
  const renumbered = renumberLines(combined);
  const newCursorPos = before.length + formattedPasted.length;

  return {
    newValue: renumbered,
    newCursorPos,
  };
};
